<template>
    <div class="grin-card-signup">
        <g-form id="grin-card-signup__form">
            <div class="g-mt-8 g-mb-6" style="text-align: center">
                <img :src="PLACEHOLDER_IMAGE" width="160px" />
            </div>
            <g-typography tag="h2" font-weight="semibold" size="h1" text-align="center" class="g-mb-7">
                Where should we send your new card?
            </g-typography>

            <g-form-item label="Full Name" class="g-mb-3">
                <g-input v-model="name" class="input" />
            </g-form-item>

            <address-input
                ref="addressInputRef"
                :address="addressData"
                :on-field-change="handleReady"
                :supported-countries="supportedCountries"
                @changed="handleReady"
            />

            <g-row type="flex" align="middle" justify="space-around" class="g-mb-6 g-mt-8">
                <g-column :span="17">
                    <g-button
                        :disabled="!ready || isLoading || isSubmitting"
                        shape="round"
                        theme="live"
                        type="primary"
                        block
                        size="large"
                        @click="handleSubmit"
                    >
                        Submit
                    </g-button>
                </g-column>
            </g-row>
            <g-row type="flex" align="middle" justify="space-around" class="g-mb-4">
                <g-column>
                    <g-link type="normal" @click="$emit('back')">Go Back</g-link>
                </g-column>
            </g-row>
        </g-form>
    </div>
</template>

<script>
    import { onMounted, ref } from 'vue-demi';
    import { get } from 'lodash';
    import { message } from '@grininc/grin-ui';
    import { PLACEHOLDER_IMAGE } from '../../../../../app-v2/constants/cdn-assets';
    import { EVENT_NAME_PAYOUTS_GRIN_PAY_ADDRESS_SUBMITTED } from '../../../../../app-v2/constants/analytics-events';
    import AddressInput from '../../../interface/form/AddressInput.vue';
    import useToggle from '../../../../composables/use-toggle';
    import usePayoutMethods from '../../../../composables/use-payout-methods';
    import useEventTracking from '../../../../composables/use-event-tracking';
    import useWhiteLabeling from '../../../../composables/use-white-labeling';
    import useContactStore from '../../../../../stores/contact';
    import usePayoutAccountStore from '../../../../../stores/payout-account';

    export default {
        name: 'GrinCardSignup',
        components: { AddressInput },
        setup(props, context) {
            const events = useEventTracking();
            const contact = useContactStore().data;
            const payoutAccount = usePayoutAccountStore().data;

            //location data
            //TODO: GN-11197 use stripe connect api to grab supported stripe connect countries
            const supportedCountries = ['US'];

            const combineFirstLast = (firstName, lastName) => {
                if (firstName && lastName) {
                    return firstName + ' ' + lastName;
                }

                if (firstName) {
                    return firstName;
                }

                return '';
            };

            const firstName = context.root.contact.first_name;
            const lastName = context.root.contact.last_name;
            const name = ref(combineFirstLast(firstName, lastName));
            const addressData = ref({
                id: get(context.root.contact, 'primary_address.id', null),
                street: get(context.root.contact, 'primary_address.street', null),
                street_2: get(context.root.contact, 'primary_address.street_2', null),
                city: get(context.root.contact, 'primary_address.city', null),
                state: get(context.root.contact, 'primary_address.state', null),
                zip: get(context.root.contact, 'primary_address.zip', null),
                country: get(context.root.contact, 'primary_address.country', null)
            });
            const addressInputRef = ref(null);

            //form state
            const { currentValue: isLoading, setTrue: loading, setFalse: doneLoading } = useToggle(true);
            const { currentValue: isSubmitting, setTrue: submitting, setFalse: doneSubmitting } = useToggle(false);
            const ready = ref(false);

            const handleReady = () => {
                if (!name.value) {
                    ready.value = false;
                    return;
                }

                if (!addressInputRef.value.isValid) {
                    ready.value = false;
                    return;
                }

                ready.value = true;
            };

            const getPayload = () => {
                addressData.value = addressInputRef.value.getPayload();
                return {
                    ...addressData.value,
                    name: name.value
                };
            };

            const handleSubmit = async () => {
                if (!ready.value) {
                    return;
                }

                submitting();

                const addressData = getPayload();

                const { issuePhysicalGrinCard: issueCard } = usePayoutMethods();
                issueCard(context.root.contact.id, addressData)
                    .then(() => {
                        doneSubmitting();
                        events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_ADDRESS_SUBMITTED, getEventPayload());
                        context.emit('success');
                    })
                    .catch((error) => {
                        message.responseError(error);
                        events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_ADDRESS_SUBMITTED, getEventPayload(), false);
                    })
                    .finally(() => {
                        doneSubmitting();
                    });
            };

            const getEventPayload = () => {
                return {
                    contact_id: contact.id,
                    is_whitelabled: useWhiteLabeling().isWhiteLabeled(),
                    has_pending_payout: contact.has_pending_payout,
                    has_additional_payout_method: payoutAccount?.external_account?.payout_methods.length > 0
                };
            };

            // lifecycle methods
            onMounted(() => {
                handleReady();
                doneLoading();
            });

            return {
                //form fields
                name,
                addressData,

                //form state
                ready,
                isLoading,
                isSubmitting,
                supportedCountries,

                //ref
                addressInputRef,

                //constants
                PLACEHOLDER_IMAGE,

                //methods
                combineFirstLast,
                getPayload,
                handleReady,
                handleSubmit
            };
        }
    };
</script>
